import { Component } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';

type AccordionContentItem = {
    type: 'title' | 'paragraph';
    text: string;
};

@Component({
    selector: 'app-solutions',
    standalone: true,
    imports: [NgForOf, NgIf],
    templateUrl: './solutions.component.html',
    styleUrls: ['./solutions.component.less'],
})
export class SolutionsComponent {
    activeIndex = 0;
    accordionItems: {
        title: string;
        image: string;
        content: AccordionContentItem[];
        active: boolean;
    }[] = [
        {
            title: 'Confirmed Requirement',
            image: 'assets/images/solutions/confirmed-requirement.png',
            active: true,
            content: [
                {
                    type: 'title',
                    text: 'Discovery & Alignment',
                },
                {
                    type: 'paragraph',
                    text: 'We start by immersing ourselves in your business ecosystem—interviewing stakeholders, auditing existing workflows, and analyzing market dynamics.',
                },
                {
                    type: 'title',
                    text: 'Requirement Definition',
                },
                {
                    type: 'paragraph',
                    text: 'Every need is documented, prioritized, and validated through collaborative workshops. We map requirements to measurable outcomes and align them with your budget, timeline, and long-term goals.',
                },
            ],
        },
        {
            title: 'Business Analyst',
            image: 'assets/images/solutions/business-analyst.png',
            active: false,
            content: [
                {
                    type: 'title',
                    text: 'Requirement Refinement',
                },
                {
                    type: 'paragraph',
                    text: 'Using techniques like use-case modeling and process flow diagrams, we turn vague ideas into precise, actionable specs. We document edge cases, integration needs, and even future-proofing requirements for scale.',
                },
                {
                    type: 'title',
                    text: 'Value Validation',
                },
                {
                    type: 'paragraph',
                    text: 'We validate requirements against your ROI targets. If a feature doesn’t align with measurable outcomes, we’ll challenge it—ensuring every dollar spent drives your success.',
                },
            ],
        },
        {
            title: 'UI/UX Design',
            image: 'assets/images/solutions/ux-design.png',
            active: false,
            content: [
                {
                    type: 'title',
                    text: 'Journey Mapping & Prototyping',
                },
                {
                    type: 'paragraph',
                    text: 'We map every step of the user journey—from first login to repeat engagement—then build interactive prototypes to test workflows.',
                },
                {
                    type: 'title',
                    text: 'Design Systems for Scale',
                },
                {
                    type: 'paragraph',
                    text: 'Our designs aren’t just pretty—they’re built on reusable, scalable design systems. This means faster future updates, consistent brand experience across platforms, and lower long-term maintenance costs.',
                },
            ],
        },
        {
            title: 'Development',
            image: 'assets/images/solutions/development.png',
            active: false,
            content: [
                {
                    type: 'paragraph',
                    text: 'Effective software development and defining the right SDLC phases are the backbone of digital transformation and long-term business success. By approaching each phase with a clear strategy, businesses can ensure their software solutions are functional, scalable, and adaptable to changing needs. Collaboration between business leaders and dedicated development teams is key to delivering impactful, future-proof software. With the right guidance, businesses can turn software development from a daunting task into a valuable investment that fosters innovation and growth.',
                },
            ],
        },
        {
            title: 'Quality Assurance',
            image: 'assets/images/solutions/quality-assurance.png',
            active: false,
            content: [
                {
                    type: 'title',
                    text: 'Automated & Manual Testing',
                },
                {
                    type: 'paragraph',
                    text: 'We use tools like Selenium for UI tests, Postman for API validation, and manual exploratory testing to catch edge cases. For a financial app, this might include stress-testing transaction workflows under peak load.',
                },
                {
                    type: 'title',
                    text: 'Defect Management & Root Cause Analysis',
                },
                {
                    type: 'paragraph',
                    text: 'Performance, security, and accessibility. We test against SLAs (e.g., “99.99% uptime”), conduct penetration testing, and ensure WCAG 2.1 compliance. Using retrospectives and process audits, we eliminate recurring problems, improving quality with every project.',
                },
            ],
        },
        {
            title: 'Build and Deployment',
            image: 'assets/images/solutions/build-and-deployment.png',
            active: false,
            content: [
                {
                    type: 'title',
                    text: 'Downtime Deployments',
                },
                {
                    type: 'paragraph',
                    text: 'For mission-critical systems, we use blue-green deployments or canary releases. This means your users stay online while new features roll out, with no disruption to their experience.',
                },
                {
                    type: 'title',
                    text: 'Launch Monitoring',
                },
                {
                    type: 'paragraph',
                    text: 'We monitor key metrics (e.g., server health, user error rates) for the first 72 hours, ready to address any post-launch issues before they impact your business.',
                },
            ],
        },
        {
            title: 'Retro',
            image: 'assets/images/solutions/retro.png',
            active: false,
            content: [
                {
                    type: 'paragraph',
                    text: 'Learning to Excel, Iteration by Iteration. Our retrospectives focus on process, not people. We celebrate wins (e.g., “Reduced deployment time by 50%!”) and tackle pain points (e.g., “Stakeholder feedback loops were slow”) with action plans. Every retro feeds into a “continuous improvement” backlog—ensuring lessons learned today shape better processes, better solutions, and better value for you tomorrow.',
                },
            ],
        },
    ];

    toggleAccordion(index: number): void {
        this.activeIndex = index;
        this.accordionItems.forEach((item) => {
            if (item !== this.accordionItems[index]) {
                item.active = false;
            }
        });
        this.accordionItems[index].active = !this.accordionItems[index].active;
    }
}
